import { Fragment, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { IconeColorido } from "./IconeColorido";
import { iconeDaCategoria } from "./icones-da-selecao";
import { CartaoDeCategoria } from "./CartaoDeCategoria";
import { CartaoDeItem } from "./CartaoDeItem";
import { AdicionarNaCategoria } from "./AdicionarNaCategoria";

export type ItemDaLista = { id: string; nome: string; categoria: string; marcado: boolean };
/** Pedido de "mostrar esta categoria" vindo do resumo; `vez` muda a cada clique, mesmo repetido. */
export type FocoNaCategoria = { categoria: string; vez: number } | null;

type Props = {
  itens: ItemDaLista[];
  onAlterar: (ids: string[], marcar: boolean) => void;
  /** "serviços" / "procedimentos" — vai no "X de Y". */
  unidade: string;
  /** Fechadas: só os cartões das categorias, abrindo uma por vez. Abertas: modo Lista ou busca. */
  fechadas: boolean;
  /** Abertas no modo Lista (não na busca): cada categoria abre e fecha pelo título. */
  recolhivel?: boolean;
  foco?: FocoNaCategoria;
  /** "+ Adicionar" no fim de cada categoria; o item novo nasce nela. */
  onAdicionarNaCategoria?: (categoria: string, nome: string) => void;
  /** Cartões menores e menos colunas — dentro de um diálogo. */
  compacta?: boolean;
};

/**
 * Coluna da seleção. Fechadas, é uma grade de cartões grandes por categoria; abrir um mostra os
 * itens logo abaixo da linha dele (o `grid-flow-row-dense` puxa os cartões seguintes pra cima e a
 * lista aberta ocupa a linha inteira). Abertas, cada categoria já vem com os itens à vista.
 */
export function GradeDeCategorias({ itens, onAlterar, unidade, fechadas, recolhivel, foco, onAdicionarNaCategoria, compacta }: Props) {
  const [aberta, setAberta] = useState<string | null>(null);
  const [recolhidas, setRecolhidas] = useState<Set<string>>(new Set());
  const blocos = useRef(new Map<string, HTMLElement>());
  const categorias = [...new Set(itens.map((i) => i.categoria))];

  useEffect(() => {
    if (!foco) return;
    setAberta(foco.categoria);
    setRecolhidas((r) => {
      const n = new Set(r);
      n.delete(foco.categoria);
      return n;
    });
    requestAnimationFrame(() => blocos.current.get(foco.categoria)?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [foco]);

  const registrar = (c: string) => (el: HTMLElement | null) => {
    if (el) blocos.current.set(c, el);
    else blocos.current.delete(c);
  };

  if (!fechadas) {
    const alternar = (c: string) =>
      setRecolhidas((r) => {
        const n = new Set(r);
        if (n.has(c)) n.delete(c);
        else n.add(c);
        return n;
      });
    const tudoRecolhido = categorias.every((c) => recolhidas.has(c));
    return (
      <div className="space-y-3">
        {recolhivel && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setRecolhidas(tudoRecolhido ? new Set() : new Set(categorias))}
              className="text-[12px] font-semibold text-brand-700 hover:underline dark:text-primary"
            >
              {tudoRecolhido ? "Expandir todas" : "Recolher todas"}
            </button>
          </div>
        )}
        {categorias.map((c) => {
          const recolhida = recolhivel && recolhidas.has(c);
          return (
            <section key={c} ref={registrar(c)} className="scroll-mt-24 rounded-xl border border-zinc-200 bg-white p-3 shadow-sm dark:border-border dark:bg-card">
              <ItensDaCategoria
                categoria={c}
                itens={itens.filter((i) => i.categoria === c)}
                unidade={unidade}
                onAlterar={onAlterar}
                comIcone
                recolhida={recolhida}
                onAlternar={recolhivel ? () => alternar(c) : undefined}
                onAdicionar={onAdicionarNaCategoria}
              />
            </section>
          );
        })}
      </div>
    );
  }

  return (
    <div className={cn("grid grid-flow-row-dense grid-cols-1 gap-2.5 sm:grid-cols-2", !compacta && "xl:grid-cols-3 min-[1800px]:grid-cols-4")}>
      {categorias.map((c) => {
        const daCategoria = itens.filter((i) => i.categoria === c);
        const marcados = daCategoria.filter((i) => i.marcado).length;
        const estaAberta = aberta === c;
        return (
          <Fragment key={c}>
            <div ref={registrar(c)} className="h-full scroll-mt-24">
              <CartaoDeCategoria
                titulo={c}
                detalhe={`${daCategoria.length} ${unidade}`}
                marcados={marcados}
                total={daCategoria.length}
                aberto={estaAberta}
                compacto={compacta}
                onClick={() => setAberta(estaAberta ? null : c)}
              />
            </div>
            {estaAberta && (
              <div data-painel-da-categoria className="col-span-full rounded-xl border border-brand-200 bg-white p-3 shadow-sm dark:border-primary/30 dark:bg-card">
                <ItensDaCategoria categoria={c} itens={daCategoria} unidade={unidade} onAlterar={onAlterar} onAdicionar={onAdicionarNaCategoria} />
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

function ItensDaCategoria({
  categoria,
  itens,
  unidade,
  onAlterar,
  comIcone,
  recolhida,
  onAlternar,
  onAdicionar,
}: {
  categoria: string;
  itens: ItemDaLista[];
  unidade: string;
  onAlterar: (ids: string[], marcar: boolean) => void;
  comIcone?: boolean;
  recolhida?: boolean;
  /** Com ele, o título abre e fecha a categoria (modo Lista). */
  onAlternar?: () => void;
  onAdicionar?: (categoria: string, nome: string) => void;
}) {
  const marcados = itens.filter((i) => i.marcado).length;
  const todos = marcados === itens.length;
  const titulo = (
    <>
      {comIcone && <IconeColorido {...iconeDaCategoria(categoria)} tamanho="sm" />}
      <span className="min-w-0 flex-1 text-[15px] font-semibold leading-snug text-zinc-900 dark:text-foreground">
        {categoria}{" "}
        <span className="text-[11.5px] font-normal tabular-nums text-zinc-500 dark:text-muted-foreground">
          · {marcados} de {itens.length} {unidade}
        </span>
      </span>
    </>
  );
  return (
    <>
      <div className={cn("flex items-center gap-2.5", !recolhida && "mb-2.5")}>
        {onAlternar ? (
          <button type="button" onClick={onAlternar} aria-expanded={!recolhida} className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
            <ChevronDown className={cn("h-4 w-4 shrink-0 text-zinc-400 transition-transform", recolhida && "-rotate-90")} />
            {titulo}
          </button>
        ) : (
          titulo
        )}
        <button
          type="button"
          onClick={() => onAlterar(itens.map((i) => i.id), !todos)}
          className="shrink-0 text-[12px] font-semibold text-brand-700 hover:underline dark:text-primary"
        >
          {todos ? "Desmarcar todos" : "Marcar todos"}
        </button>
      </div>
      {!recolhida && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
          {itens.map((i) => (
            <CartaoDeItem key={i.id} titulo={i.nome} estado={i.marcado ? "marcado" : "normal"} onClick={() => onAlterar([i.id], !i.marcado)} />
          ))}
          {onAdicionar && <AdicionarNaCategoria categoria={categoria} onAdicionar={(nome) => onAdicionar(categoria, nome)} />}
        </div>
      )}
    </>
  );
}
